import { useSetAtom } from 'jotai';
import { useAtomCallback } from 'jotai/utils';
import { useCallback } from 'react';

import { logger } from '../datadog/logger';
import { toast } from '../toasts';
import { Stopwatch, trySafely } from '../utils';
import {
  DEFAULT_VIDEO_SDK_ATOM_VALUE,
  LocalMediaStatus,
  VideoPlatform,
  localMediaStatusesAtom,
  normalizeVideoPlatform,
  useConnectAudioVideoInputs,
  useInitializeVideoSDKAdapter,
  videoPlatformsMatch,
  videoSDKAdapterAtom,
  videoSDKAtom,
} from '../video-sdk';
import { getVideoPlatform } from './useVideoPlatform';

/**
 * Switches the provider's video platform in-place (no page reload).
 * Initializes the new adapter before tearing down the old one,
 * ensuring the atom is never null and subscribed components don't throw.
 */
function useCoerceProviderVideoPlatform() {
  const getAdapter = useAtomCallback(useCallback((get) => get(videoSDKAdapterAtom), []));
  const getVideoSDKAtom = useAtomCallback(useCallback((get) => get(videoSDKAtom), []));
  const setVideoSDK = useSetAtom(videoSDKAtom);
  const initializeVideoSDKAdapter = useInitializeVideoSDKAdapter();
  const connectAudioVideoInputs = useConnectAudioVideoInputs();
  const setLocalMediaStatuses = useSetAtom(localMediaStatusesAtom);

  return useCallback(
    async (targetPlatform: VideoPlatform) => {
      const previousVideoSDK = getVideoSDKAtom();
      const adapter = getAdapter();

      setVideoSDK({
        ...DEFAULT_VIDEO_SDK_ATOM_VALUE,
        localAudioContext: { status: LocalMediaStatus.LOADING },
        localVideoContext: { status: LocalMediaStatus.LOADING },
      });

      try {
        await initializeVideoSDKAdapter(targetPlatform);
        await trySafely(adapter.tearDown(), (error) => {
          logger.error(
            'Failed to tear down previous adapter',
            {
              fromPlatform: getVideoPlatform(adapter),
              toPlatform: targetPlatform,
            },
            error,
          );
        });
        await trySafely(connectAudioVideoInputs());
      } finally {
        setLocalMediaStatuses((current) => {
          const { audio: currentAudioStatus, video: currentVideoStatus } = current;
          const {
            localAudioContext: { status: previousAudioStatus },
            localVideoContext: { status: previousVideoStatus },
          } = previousVideoSDK;

          return {
            audio: currentAudioStatus === LocalMediaStatus.LOADING ? previousAudioStatus : currentAudioStatus,
            video: currentVideoStatus === LocalMediaStatus.LOADING ? previousVideoStatus : currentVideoStatus,
          };
        });
      }
    },
    [
      getVideoSDKAtom,
      getAdapter,
      setVideoSDK,
      initializeVideoSDKAdapter,
      connectAudioVideoInputs,
      setLocalMediaStatuses,
    ],
  );
}

/**
 * Wraps {@link useCoerceProviderVideoPlatform} with graceful error handling.
 */
export function useGracefullyCoerceProviderVideoPlatform() {
  const coerce = useCoerceProviderVideoPlatform();

  return useCallback(
    async (props: {
      visitorVideoPlatform: VideoPlatform | null;
      providerVideoPlatform: VideoPlatform | null;
    }): Promise<boolean> => {
      const { visitorVideoPlatform, providerVideoPlatform } = props;

      const videoPlatformsAreMatching = videoPlatformsMatch(visitorVideoPlatform, providerVideoPlatform);
      if (videoPlatformsAreMatching) {
        // no-op, early exit
        return true;
      }

      const targetPlatform = normalizeVideoPlatform(visitorVideoPlatform);
      const loggerPayload = {
        fromPlatform: providerVideoPlatform,
        targetPlatform,
      };

      logger.info('Coercing provider video platform', loggerPayload);
      const stopwatch = Stopwatch.start();

      try {
        await coerce(targetPlatform);

        const durationInMs = stopwatch.stop();
        logger.info('Successfully coerced provider video platform', {
          durationInMs,
          ...loggerPayload,
        });

        return true;
      } catch (error) {
        logger.error(
          'Failed to coerce provider video platform',
          loggerPayload,
          error instanceof Error ? error : undefined,
        );
        toast.error('Unable to admit due to an unexpected error. Please refresh if this issue persists.');

        return false;
      }
    },
    [coerce],
  );
}
